/**
 * Procedural garments that fit any body: "measured lofts". At every ring height (or along a limb) we slice the body's
 * rest mesh with a plane, measure its radius per angle around the garment axis, add clearance and build rings → a clean
 * low-poly cloth surface whatever the base model's proportions. Garment vertices copy skin weights from the nearest
 * body vertices of the allowed bones (skirts blend with the pelvis), so they follow morphs and animation; body
 * triangles fully covered by a garment are dropped from the body's index list (no poke-through, less overdraw).
 * Rigid hats are lathes sized from the measured head ring and bound 100 % to the head bone.
 */
import type { Landmarks, Skeleton, V3 } from './rig';

export interface Body {
  pos: Float32Array;
  indices: Uint32Array;
  owner: Uint8Array;
  joints: Uint8Array;
  weights: Float32Array;
  sk: Skeleton;
  lm: Landmarks;
}

export interface ClothStyle {
  base: string; trim: string; accent: string;
  /** 0 linen, 1 cross, 2 lozenge, 3 mi-parti, 4 metal, 5 mail, 6 ermine, 7 velvet with pale border, 8 rope */
  pattern: number;
  trimWidth: number;
  folds: number;
}

export interface GarmentPiece {
  id: string;
  positions: number[]; uv: number[]; joints: number[]; weights: number[]; indices: number[];
  style: ClothStyle;
}

export const BIN = 24;

// ── body slicing ──────────────────────────────────────────────────────────────────────────────────────────────────
/** Points where body triangles (owned by `bones`) cross the plane through `c` with normal `n`. */
export function slice(body: Body, bones: Set<number>, c: V3, n: V3, maxDist: number): V3[] {
  const out: V3[] = [];
  const P = body.pos, I = body.indices;
  const sd = (i: number) => (P[i * 3] - c[0]) * n[0] + (P[i * 3 + 1] - c[1]) * n[1] + (P[i * 3 + 2] - c[2]) * n[2];
  for (let t = 0; t < I.length; t += 3) {
    const a = I[t], b = I[t + 1], cc = I[t + 2];
    if (!bones.has(body.owner[a]) && !bones.has(body.owner[b]) && !bones.has(body.owner[cc])) continue;
    const da = sd(a), db = sd(b), dc = sd(cc);
    const edges: [number, number, number, number][] = [[a, b, da, db], [b, cc, db, dc], [cc, a, dc, da]];
    for (const [i, j, di, dj] of edges) {
      if ((di > 0) === (dj > 0)) continue;
      const k = di / (di - dj);
      // A triangle may straddle the body/tail ownership boundary. Its third
      // vertex owning the pelvis must not make a far tail edge part of a waist
      // measurement: that produces a long rigid shelf in every measured layer.
      const allowed = (bones.has(body.owner[i]) ? 1 - k : 0) + (bones.has(body.owner[j]) ? k : 0);
      if (allowed < 0.5) continue;
      const p: V3 = [P[i * 3] + (P[j * 3] - P[i * 3]) * k, P[i * 3 + 1] + (P[j * 3 + 1] - P[i * 3 + 1]) * k, P[i * 3 + 2] + (P[j * 3 + 2] - P[i * 3 + 2]) * k];
      if (Math.hypot(p[0] - c[0], p[1] - c[1], p[2] - c[2]) <= maxDist) out.push(p);
    }
  }
  return out;
}

/** Radius per angle bin around centre c in basis (e1, e2); empty bins interpolated, then smoothed. */
export function envelope(points: V3[], c: V3, e1: V3, e2: V3, minR: number, bins = BIN): Float32Array {
  const r = new Float32Array(bins).fill(-1);
  for (const p of points) {
    const d: V3 = [p[0] - c[0], p[1] - c[1], p[2] - c[2]];
    const x = d[0] * e1[0] + d[1] * e1[1] + d[2] * e1[2], y = d[0] * e2[0] + d[1] * e2[1] + d[2] * e2[2];
    const a = Math.atan2(y, x);
    const b = Math.floor(((a + Math.PI) / (2 * Math.PI)) * bins) % bins;
    r[b] = Math.max(r[b], Math.hypot(x, y));
  }
  const filled = [...r];
  if (filled.every((x) => x < 0)) return new Float32Array(bins).fill(minR);
  for (let i = 0; i < bins; i++) {
    if (filled[i] >= 0) continue;
    let lo = 1, hi = 1;
    while (r[(i - lo + bins) % bins] < 0) lo++;
    while (r[(i + hi) % bins] < 0) hi++;
    const a = r[(i - lo + bins) % bins], b = r[(i + hi) % bins];
    filled[i] = a + (b - a) * (lo / (lo + hi));
  }
  const sm = new Float32Array(bins);
  for (let i = 0; i < bins; i++) sm[i] = Math.max(minR, (filled[(i - 1 + bins) % bins] + 2 * filled[i] + filled[(i + 1) % bins]) / 4);
  // The two interpolated samples surrounding every measured angle must stay
  // outside it. Averaging alone shrank peaks and made layered fabric intersect.
  const out = new Float32Array(bins);
  for (let i = 0; i < bins; i++) out[i] = Math.max(sm[i], filled[i], filled[(i - 1 + bins) % bins], filled[(i + 1) % bins]);
  return out;
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const lerp3 = (a: V3, b: V3, t: number): V3 => [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];
const norm = (v: V3): V3 => { const l = Math.hypot(v[0], v[1], v[2]) || 1; return [v[0] / l, v[1] / l, v[2] / l]; };
const cross = (a: V3, b: V3): V3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
export const sampleR = (r: Float32Array, a: number) => {
  const bins = r.length;
  const f = ((a + Math.PI) / (2 * Math.PI)) * bins - 0.5;
  const i0 = Math.floor(f), t = f - i0;
  return lerp(r[((i0 % bins) + bins) % bins], r[(((i0 + 1) % bins) + bins) % bins], t);
};

// ── loft builder ──────────────────────────────────────────────────────────────────────────────────────────────────
interface Ring { c: V3; e1: V3; e2: V3; r: Float32Array; v: number }

/** Rings must progress along e1 × e2 (true for horizontal rings top → bottom and limb rings root → tip). */
function loft(rings: Ring[], segs: number, arc: [number, number] | null): { positions: number[]; uv: number[]; indices: number[] } {
  const positions: number[] = [], uv: number[] = [], indices: number[] = [];
  const closed = arc === null;
  const cols = closed ? segs : segs + 1;
  for (const ring of rings) {
    for (let j = 0; j < cols; j++) {
      const u = j / segs;
      const a = closed ? -Math.PI + u * 2 * Math.PI : lerp(arc![0], arc![1], u);
      const rr = sampleR(ring.r, a);
      const dir: V3 = [Math.cos(a) * ring.e1[0] + Math.sin(a) * ring.e2[0], Math.cos(a) * ring.e1[1] + Math.sin(a) * ring.e2[1], Math.cos(a) * ring.e1[2] + Math.sin(a) * ring.e2[2]];
      positions.push(ring.c[0] + dir[0] * rr, ring.c[1] + dir[1] * rr, ring.c[2] + dir[2] * rr);
      uv.push((a + Math.PI) / (2 * Math.PI), ring.v);
    }
  }
  for (let i = 0; i < rings.length - 1; i++) {
    for (let j = 0; j < segs; j++) {
      const j1 = closed ? (j + 1) % segs : j + 1;
      const a = i * cols + j, b = i * cols + j1, c = (i + 1) * cols + j, d = (i + 1) * cols + j1;
      indices.push(a, b, c, b, d, c);
    }
  }
  return { positions, uv, indices };
}

export const H_E1: V3 = [1, 0, 0], H_E2: V3 = [0, 0, 1];

// ── skinning ──────────────────────────────────────────────────────────────────────────────────────────────────────
export function bindToBody(body: Body, positions: number[], bones: Set<number>, blendBone = -1, blend = 0): { joints: number[]; weights: number[] } {
  const P = body.pos, vc = P.length / 3;
  const cand: number[] = [];
  for (let v = 0; v < vc; v++) if (bones.has(body.owner[v])) cand.push(v);
  const joints: number[] = [], weights: number[] = [];
  for (let g = 0; g < positions.length / 3; g++) {
    const x = positions[g * 3], y = positions[g * 3 + 1], z = positions[g * 3 + 2];
    let best = cand[0] ?? 0, bd = Infinity;
    for (const v of cand) { const d = (P[v * 3] - x) ** 2 + (P[v * 3 + 1] - y) ** 2 + (P[v * 3 + 2] - z) ** 2; if (d < bd) { bd = d; best = v; } }
    const w: Map<number, number> = new Map();
    for (let k = 0; k < 4; k++) { const wk = body.weights[best * 4 + k]; if (wk > 0) w.set(body.joints[best * 4 + k], (w.get(body.joints[best * 4 + k]) ?? 0) + wk * (1 - blend)); }
    if (blendBone >= 0 && blend > 0) w.set(blendBone, (w.get(blendBone) ?? 0) + blend);
    const top = [...w.entries()].sort((a, b) => b[1] - a[1]).slice(0, 4);
    const s = top.reduce((a, e) => a + e[1], 0) || 1;
    for (let k = 0; k < 4; k++) { joints.push(top[k]?.[0] ?? 0); weights.push(top[k] ? top[k][1] / s : 0); }
  }
  return { joints, weights };
}

// ── helpers on the skeleton ───────────────────────────────────────────────────────────────────────────────────────
export function boneSet(sk: Skeleton, names: string[]): Set<number> {
  const s = new Set<number>();
  for (const n of names) { const i = sk.byName.get(n); if (i !== undefined) s.add(i); }
  return s;
}
/** Torso axis centre at height y (pelvis → chest → neck polyline). */
export function torsoCentre(lm: Landmarks, y: number): V3 {
  const pts = [lm.pelvis, lm.chest, lm.neck, lm.head];
  if (y <= pts[0][1]) return [pts[0][0], y, pts[0][2]];
  for (let i = 0; i < pts.length - 1; i++) if (y <= pts[i + 1][1]) { const t = (y - pts[i][1]) / (pts[i + 1][1] - pts[i][1]); const c = lerp3(pts[i], pts[i + 1], t); return [c[0], y, c[2]]; }
  return [pts[3][0], y, pts[3][2]];
}

export interface Fit {
  piece: GarmentPiece;
  /** Body vertices this piece fully covers (their triangles can be hidden). */
  covers: (v: number) => boolean;
}

// ── garments ──────────────────────────────────────────────────────────────────────────────────────────────────────
const TORSO = ['pelvis', 'chest', 'neck'];
const LEGS_UP = ['thighR', 'thighL'];
const LEGS = ['thighR', 'thighL', 'shinR', 'shinL'];

/** A body-hugging tunic (neck → hem), optional skirt flare below the hips and sleeves. */
export function tunic(body: Body, opts: { id: string; hem: number; flare: number; clearance: number; sleeves: 'none' | 'short' | 'long' | 'bell'; style: ClothStyle; collar?: number }): Fit[] {
  const { lm, sk } = body;
  const torsoBones = boneSet(sk, [...TORSO, ...LEGS]);
  const neckY = lerp(lm.chest[1], lm.neck[1], 0.86);
  const hipY = lm.hipR[1];
  const ankleY = lm.ankleR[1];
  const hemY = lerp(hipY, ankleY, opts.hem); // hem 0 = at the hips, 1 = ankles
  const rings: Ring[] = [];
  const N = 9;
  let prevR: Float32Array | null = null;
  for (let i = 0; i <= N; i++) {
    const t = i / N;
    const y = lerp(neckY, hemY, t);
    const c = torsoCentre(lm, y);
    const below = y < hipY;
    const pts = slice(body, torsoBones, c, [0, 1, 0], 0.4);
    let r = envelope(pts, c, H_E1, H_E2, 0.03);
    for (let k = 0; k < BIN; k++) r[k] += opts.clearance;
    if (below && prevR) {
      // skirts hang: never narrower than the ring above, plus a flare growing toward the hem
      const k2 = (hipY - y) / Math.max(1e-3, hipY - hemY);
      for (let k = 0; k < BIN; k++) r[k] = Math.max(r[k], prevR[k]) + opts.flare * k2 * 0.06;
    }
    if (i === 0 && opts.collar) for (let k = 0; k < BIN; k++) r[k] += opts.collar;
    rings.push({ c, e1: H_E1, e2: H_E2, r, v: t });
    prevR = r;
  }
  const geo = loft(rings, 20, null);
  const pelvis = sk.byName.get('pelvis')!;
  const skin = bindToBody(body, geo.positions, torsoBones);
  // skirt part follows the pelvis more than the legs
  for (let g = 0; g < geo.positions.length / 3; g++) {
    const y = geo.positions[g * 3 + 1];
    if (y >= hipY) continue;
    const k = Math.min(1, (hipY - y) / Math.max(1e-3, hipY - hemY)) * 0.55;
    const w = new Map<number, number>();
    for (let q = 0; q < 4; q++) w.set(skin.joints[g * 4 + q], (w.get(skin.joints[g * 4 + q]) ?? 0) + skin.weights[g * 4 + q] * (1 - k));
    w.set(pelvis, (w.get(pelvis) ?? 0) + k);
    const top = [...w.entries()].sort((a, b) => b[1] - a[1]).slice(0, 4);
    const s = top.reduce((a, e) => a + e[1], 0) || 1;
    for (let q = 0; q < 4; q++) { skin.joints[g * 4 + q] = top[q]?.[0] ?? 0; skin.weights[g * 4 + q] = top[q] ? top[q][1] / s : 0; }
  }
  const fits: Fit[] = [{
    piece: { id: opts.id, ...geo, ...skin, style: opts.style },
    covers: (v) => { const y = body.pos[v * 3 + 1]; return torsoBones.has(body.owner[v]) && y < neckY - 0.015 && y > hemY + 0.02; },
  }];
  if (opts.sleeves !== 'none') for (const s of ['R', 'L'] as const) fits.push(sleeve(body, s, opts));
  return fits;
}

function sleeve(body: Body, side: 'R' | 'L', opts: { id: string; clearance: number; sleeves: 'none' | 'short' | 'long' | 'bell'; style: ClothStyle }): Fit {
  const { lm, sk } = body;
  const sh = side === 'R' ? lm.shoulderR : lm.shoulderL, el = side === 'R' ? lm.elbowR : lm.elbowL, wr = side === 'R' ? lm.wristR : lm.wristL;
  const bones = boneSet(sk, [`upperArm${side}`, `foreArm${side}`]);
  const path: V3[] = opts.sleeves === 'short' ? [sh, lerp3(sh, el, 0.6)] : [sh, el, lerp3(el, wr, 0.92)];
  const rings: Ring[] = [];
  const N = opts.sleeves === 'short' ? 3 : 7;
  const total = path.length - 1;
  for (let i = 0; i <= N; i++) {
    const t = (i / N) * total;
    const seg = Math.min(total - 1, Math.floor(t)), k = t - seg;
    const c = lerp3(path[seg], path[seg + 1], k);
    const axis = norm([path[seg + 1][0] - path[seg][0], path[seg + 1][1] - path[seg][1], path[seg + 1][2] - path[seg][2]]);
    const e1 = norm(cross(axis, [0, 0, 1])); const e2 = norm(cross(axis, e1));
    const pts = slice(body, bones, c, axis, 0.09);
    const r = envelope(pts, c, e1, e2, 0.018);
    const bell = opts.sleeves === 'bell' ? Math.pow(i / N, 2) * 0.035 : 0;
    for (let q = 0; q < BIN; q++) r[q] += opts.clearance * 0.8 + bell + (i === 0 ? 0.01 : 0);
    rings.push({ c, e1, e2, r, v: i / N });
  }
  const geo = loft(rings, 12, null);
  const skin = bindToBody(body, geo.positions, bones);
  const end = path[path.length - 1];
  return {
    piece: { id: `${opts.id}-sleeve${side}`, ...geo, ...skin, style: opts.style },
    covers: (v) => {
      if (!bones.has(body.owner[v])) return false;
      const p: V3 = [body.pos[v * 3], body.pos[v * 3 + 1], body.pos[v * 3 + 2]];
      const ax = norm([end[0] - sh[0], end[1] - sh[1], end[2] - sh[2]]);
      const proj = (p[0] - sh[0]) * ax[0] + (p[1] - sh[1]) * ax[1] + (p[2] - sh[2]) * ax[2];
      const len = Math.hypot(end[0] - sh[0], end[1] - sh[1], end[2] - sh[2]);
      return proj > 0.02 && proj < len - 0.02;
    },
  };
}

/** A cape hanging from the shoulders across the back (an open arc), to `hem` (0 hips … 1 ankles). */
export function cape(body: Body, opts: { id: string; hem: number; style: ClothStyle }): Fit {
  const { lm, sk } = body;
  const bones = boneSet(sk, [...TORSO, ...LEGS, 'upperArmR', 'upperArmL']);
  const topY = lerp(lm.chest[1], lm.neck[1], 0.75);
  const hemY = lerp(lm.hipR[1], lm.ankleR[1], opts.hem);
  const rings: Ring[] = [];
  const N = 10;
  let prev: Float32Array | null = null;
  for (let i = 0; i <= N; i++) {
    const t = i / N;
    const y = lerp(topY, hemY, t);
    const c = torsoCentre(lm, y);
    const pts = slice(body, bones, c, [0, 1, 0], 0.5);
    const r = envelope(pts, c, H_E1, H_E2, 0.05);
    for (let k = 0; k < BIN; k++) r[k] += 0.03 + t * 0.03;
    if (prev) for (let k = 0; k < BIN; k++) r[k] = Math.max(r[k], prev[k] * 0.97);
    rings.push({ c, e1: H_E1, e2: H_E2, r, v: t });
    prev = r;
  }
  // back arc: from the right shoulder round the back to the left shoulder (θ = −π/2 is the back)
  const geo = loft(rings, 18, [-Math.PI - 0.25, 0.25]); // right shoulder → back (θ = −π/2) → left shoulder
  const chest = sk.byName.get('chest')!;
  const skin = bindToBody(body, geo.positions, boneSet(sk, [...TORSO]), chest, 0.35);
  return { piece: { id: opts.id, ...geo, ...skin, style: opts.style }, covers: () => false };
}

/** Hood with a face opening and a short shoulder capelet. */
export function hood(body: Body, opts: { id: string; style: ClothStyle; point: number }): Fit[] {
  const { lm, sk } = body;
  const headBones = boneSet(sk, ['head', 'neck']);
  const rings: Ring[] = [];
  const top = lm.headTop[1] + 0.005, base = lerp(lm.neck[1], lm.chest[1], 0.25);
  const N = 9;
  for (let i = 0; i <= N; i++) {
    const t = i / N;
    const y = lerp(top - 0.035, base, t);
    const c: V3 = [lerp(lm.head[0], lm.neck[0], Math.max(0, t - 0.4)), y, lerp(lm.head[2], lm.neck[2], t) - 0.01];
    const pts = slice(body, headBones, c, [0, 1, 0], 0.25);
    const r = envelope(pts, c, H_E1, H_E2, 0.04);
    for (let k = 0; k < BIN; k++) r[k] += 0.016 + (i === 0 ? -0.02 : 0);
    rings.push({ c, e1: H_E1, e2: H_E2, r, v: t });
  }
  // face opening: an arc that skips the front (θ ≈ +π/2) above the chin
  const geo = loft(rings, 18, [Math.PI / 2 + 1.05, Math.PI / 2 + 2 * Math.PI - 1.05]);
  // pointed liripipe: pull the top-back vertices up/back
  for (let g = 0; g < geo.positions.length / 3; g++) {
    const v = geo.uv[g * 2 + 1];
    if (v < 0.25) { const k = (0.25 - v) / 0.25; geo.positions[g * 3 + 1] += k * k * opts.point * 0.12; geo.positions[g * 3 + 2] -= k * k * opts.point * 0.08; }
  }
  const skin = bindToBody(body, geo.positions, headBones);
  const fits: Fit[] = [{ piece: { id: opts.id, ...geo, ...skin, style: opts.style }, covers: () => false }];
  // capelet over the shoulders
  const shoulder = tunicCapelet(body, opts);
  fits.push(shoulder);
  return fits;
}

function tunicCapelet(body: Body, opts: { id: string; style: ClothStyle }): Fit {
  const { lm, sk } = body;
  const bones = boneSet(sk, [...TORSO, 'upperArmR', 'upperArmL']);
  const rings: Ring[] = [];
  const topY = lerp(lm.chest[1], lm.neck[1], 0.95), botY = lerp(lm.chest[1], lm.neck[1], 0.15);
  for (let i = 0; i <= 4; i++) {
    const t = i / 4, y = lerp(topY, botY, t);
    const c = torsoCentre(lm, y);
    const pts = slice(body, bones, c, [0, 1, 0], 0.4);
    const r = envelope(pts, c, H_E1, H_E2, 0.04);
    for (let k = 0; k < BIN; k++) r[k] += 0.02 + t * 0.03;
    rings.push({ c, e1: H_E1, e2: H_E2, r, v: t });
  }
  const geo = loft(rings, 20, null);
  const skin = bindToBody(body, geo.positions, boneSet(sk, [...TORSO]));
  return { piece: { id: `${opts.id}-capelet`, ...geo, ...skin, style: opts.style }, covers: () => false };
}

/** Belt / ruff: a narrow ring at a height on the torso. */
export function ring(body: Body, opts: { id: string; at: 'waist' | 'neck'; width: number; puff: number; style: ClothStyle; pleats?: number }): Fit {
  const { lm, sk } = body;
  const bones = boneSet(sk, [...TORSO, ...LEGS_UP]);
  const y0 = opts.at === 'waist' ? lerp(lm.pelvis[1], lm.chest[1], 0.35) : lerp(lm.chest[1], lm.neck[1], 0.92);
  const rings: Ring[] = [];
  for (let i = 0; i <= 2; i++) {
    const y = y0 + (1 - i) * opts.width * 0.5;
    const c = torsoCentre(lm, y);
    const pts = slice(body, bones, c, [0, 1, 0], 0.4);
    const r = envelope(pts, c, H_E1, H_E2, 0.03);
    for (let k = 0; k < BIN; k++) r[k] += 0.02 + (i === 1 ? opts.puff : 0);
    rings.push({ c, e1: H_E1, e2: H_E2, r, v: i / 2 });
  }
  const geo = loft(rings, opts.at === 'neck' ? 28 : 20, null);
  const pleats = opts.pleats ?? (opts.at === 'neck' ? 14 : 0);
  if (pleats > 0) for (let g = 0; g < geo.positions.length / 3; g++) {
    // ruff pleats: alternate radial puffing
    const u = geo.uv[g * 2];
    const k = Math.cos(u * Math.PI * 2 * pleats) * 0.5 + 0.5;
    const c = torsoCentre(lm, geo.positions[g * 3 + 1]);
    geo.positions[g * 3] = c[0] + (geo.positions[g * 3] - c[0]) * (1 + k * 0.12);
    geo.positions[g * 3 + 2] = c[2] + (geo.positions[g * 3 + 2] - c[2]) * (1 + k * 0.12);
  }
  const skin = bindToBody(body, geo.positions, boneSet(sk, opts.at === 'waist' ? ['pelvis', 'chest'] : ['chest', 'neck']));
  return { piece: { id: opts.id, ...geo, ...skin, style: opts.style }, covers: () => false };
}

// ── rigid hats ────────────────────────────────────────────────────────────────────────────────────────────────────
/** Lathe from a (radius, height) profile around the head, measured at the hat band. */
export function hat(body: Body, opts: { id: string; kind: 'crown' | 'mitre' | 'helm' | 'cap'; style: ClothStyle }): Fit {
  const { lm, sk } = body;
  const headBones = boneSet(sk, ['head']);
  const mappedRoots = [lm.earRPath?.[0], lm.earLPath?.[0]].filter((p): p is V3 => !!p);
  const earRoots = mappedRoots.length ? mappedRoots.map(p => p[1]) : sk.bones.filter(b => b.chain === 'ear' && sk.bones[b.parent]?.chain === 'head').map(b => b.head[1]);
  // Several source headTop landmarks include the pointed ears. Seat the band
  // against the cranium below their roots, so a large head never wears a halo.
  const seatedBand = Math.min(lerp(lm.head[1], lm.headTop[1], 0.62), earRoots.length ? Math.max(...earRoots) - 0.008 : Infinity);
  // The helmet profile drops 0.04 below its measuring band. Clear the fitted
  // brows rather than using one forehead height for every imported cat face.
  const bandY = opts.kind==='helm'&&lm.face?Math.max(seatedBand,lm.face.center[1]+lm.face.scale*.65+.04):seatedBand;
  const c: V3 = [lm.head[0], bandY, lm.head[2]];
  const pts = slice(body, headBones, c, [0, 1, 0], 0.3);
  const band = envelope(pts, c, H_E1, H_E2, 0.05);
  const meanR = band.reduce((a, b) => a + b, 0) / BIN;
  const profiles: Record<string, [number, number][]> = {
    // (radius scale relative to the band, height above the band in model heights)
    crown: [[1.04, -0.012], [1.06, 0.03], [1.08, 0.075]],
    mitre: [[1.02, -0.015], [1.05, 0.05], [0.95, 0.13], [0.55, 0.2], [0.05, 0.24]],
    helm: [[1.1, -0.04], [1.12, 0.0], [1.06, 0.05], [0.82, 0.09], [0.4, 0.112], [0.02, 0.118]],
    cap: [[1.06, -0.01], [1.1, 0.03], [0.85, 0.075], [0.02, 0.09]],
  };
  const prof = profiles[opts.kind];
  const crownHeight = opts.kind === 'helm' ? Math.max(0.58, Math.min(1.25, meanR / 0.105)) : 1;
  const rings: Ring[] = prof.map(([s, h], i) => {
    const r = new Float32Array(BIN);
    for (let k = 0; k < BIN; k++) r[k] = Math.max(0.002, band[k] * 0.5 + meanR * 0.5) * s;
    if (opts.kind === 'mitre') for (let k = 0; k < BIN; k++) { const a = -Math.PI + (k + 0.5) / BIN * 2 * Math.PI; r[k] *= lerp(1, 0.55 + 0.45 * Math.abs(Math.sin(a)), Math.min(1, h / 0.12)); }
    return { c: [c[0], bandY + (h > 0 ? h * crownHeight : h), c[2] - (opts.kind === 'helm' ? 0.005 : 0)] as V3, e1: H_E1, e2: H_E2, r, v: i / (prof.length - 1) };
  }).reverse(); // top → bottom for outward winding
  const geo = loft(rings, opts.kind === 'crown' ? 24 : 20, null);
  if (opts.kind === 'crown') {
    // crown points: lift every other column on the top ring
    const cols = 24;
    for (let j = 0; j < cols; j++) if (j % 3 === 0) geo.positions[j * 3 + 1] += 0.05;
  }
  const head = sk.byName.get('head')!;
  const joints: number[] = [], weights: number[] = [];
  for (let g = 0; g < geo.positions.length / 3; g++) { joints.push(head, 0, 0, 0); weights.push(1, 0, 0, 0); }
  return { piece: { id: opts.id, ...geo, joints, weights, style: opts.style }, covers: () => false };
}

// ── outfits ───────────────────────────────────────────────────────────────────────────────────────────────────────
export interface Outfit { id: string; name: string }
export const OUTFITS: Outfit[] = [
  { id: 'none', name: 'Нагой' },
  { id: 'crusader', name: 'Крестоносец' },
  { id: 'monk', name: 'Монах' },
  { id: 'king', name: 'Король' },
  { id: 'bishop', name: 'Епископ' },
  { id: 'wanderer', name: 'Бродяга' },
  { id: 'jester', name: 'Шут' },
];

const S = (base: string, trim: string, accent: string, pattern: number, trimWidth = 0.07, folds = 0): ClothStyle => ({ base, trim, accent, pattern, trimWidth, folds });

export function buildOutfit(body: Body, id: string): Fit[] {
  switch (id) {
    case 'crusader': return [
      ...tunic(body, { id: 'tabard', hem: 0.55, flare: 0.35, clearance: 0.012, sleeves: 'none', style: S('#efe6d2', '#9c7a22', '#b3261e', 1, 0.05, 6) }),
      cape(body, { id: 'cape', hem: 0.8, style: S('#9e2a1c', '#c9a13b', '#e9dcc0', 0, 0.04, 9) }),
      ring(body, { id: 'belt', at: 'waist', width: 0.03, puff: 0.004, style: S('#4a2c17', '#c9a13b', '#c9a13b', 0, 0.2) }),
      hat(body, { id: 'helm', kind: 'helm', style: S('#9aa3ad', '#c9a13b', '#2b1d14', 4, 0.12) }),
    ];
    case 'monk': return [
      ...tunic(body, { id: 'robe', hem: 0.95, flare: 0.7, clearance: 0.014, sleeves: 'bell', style: S('#6b4a2e', '#4a3220', '#c9a13b', 0, 0.03, 10) }),
      ...hood(body, { id: 'hood', point: 0.3, style: S('#5e4027', '#4a3220', '#c9a13b', 0, 0.04, 4) }),
      ring(body, { id: 'rope', at: 'waist', width: 0.02, puff: 0.006, style: S('#d8c39a', '#b59a68', '#b59a68', 0, 0.3) }),
    ];
    case 'king': return [
      ...tunic(body, { id: 'gown', hem: 0.9, flare: 0.5, clearance: 0.013, sleeves: 'long', style: S('#24418f', '#c9a13b', '#c9a13b', 2, 0.06, 7) }),
      cape(body, { id: 'mantle', hem: 1.0, style: S('#a8251a', '#f4efe6', '#1b1b1b', 0, 0.12, 10) }),
      ring(body, { id: 'ruff', at: 'neck', width: 0.03, puff: 0.012, style: S('#f4efe6', '#d8d0c0', '#1b1b1b', 0, 0.0) }),
      hat(body, { id: 'crown', kind: 'crown', style: S('#d9a92e', '#b3261e', '#2650a8', 4, 0.15) }),
    ];
    case 'bishop': return [
      ...tunic(body, { id: 'alb', hem: 1.0, flare: 0.6, clearance: 0.013, sleeves: 'long', style: S('#f2ece0', '#c9a13b', '#b3261e', 0, 0.05, 9) }),
      cape(body, { id: 'cope', hem: 0.85, style: S('#c08a1e', '#9e2a1c', '#9e2a1c', 2, 0.08, 6) }),
      hat(body, { id: 'mitre', kind: 'mitre', style: S('#f2ece0', '#c9a13b', '#b3261e', 1, 0.1) }),
    ];
    case 'wanderer': return [
      ...tunic(body, { id: 'jerkin', hem: 0.35, flare: 0.4, clearance: 0.012, sleeves: 'short', style: S('#3f6b45', '#2b1d14', '#c9a13b', 0, 0.05, 5) }),
      ...hood(body, { id: 'cowl', point: 0.55, style: S('#2f5a37', '#2b1d14', '#c9a13b', 0, 0.05, 3) }),
      ring(body, { id: 'belt', at: 'waist', width: 0.025, puff: 0.004, style: S('#5a3a1e', '#c9a13b', '#c9a13b', 0, 0.2) }),
    ];
    case 'jester': return [
      ...tunic(body, { id: 'motley', hem: 0.4, flare: 0.5, clearance: 0.012, sleeves: 'long', style: S('#b3261e', '#c9a13b', '#24418f', 3, 0.05, 0) }),
      ring(body, { id: 'ruff', at: 'neck', width: 0.035, puff: 0.016, style: S('#c9a13b', '#b3261e', '#24418f', 0, 0) }),
      hat(body, { id: 'cap', kind: 'cap', style: S('#24418f', '#c9a13b', '#b3261e', 3, 0.12) }),
    ];
    default: return [];
  }
}

/** A leather cat collar with a little gold bell (hides the seam of a grafted head). */
export function catCollar(body: Body): Fit[] {
  const collar = ring(body, { id: 'collar', at: 'neck', width: 0.022, puff: 0.002, pleats: 0, style: S('#7a2a17', '#c9a13b', '#c9a13b', 0, 0.18) });
  const { lm, sk } = body;
  const y = lm.chest[1] + (lm.neck[1] - lm.chest[1]) * 0.92 - 0.018;
  const c = torsoCentre(lm, y);
  const pts = slice(body, boneSet(sk, ['chest', 'neck']), c, [0, 1, 0], 0.4);
  const r = envelope(pts, c, H_E1, H_E2, 0.03);
  const front = sampleR(r, Math.PI / 2) + 0.03;
  const bc: V3 = [c[0], y - 0.012, c[2] + front];
  const rad = 0.016;
  const prof: [number, number][] = [[0.1, 1], [0.75, 0.75], [1, 0.1], [0.95, -0.45], [0.7, -0.8], [0.02, -0.9]];
  const rings: Ring[] = prof.map(([s, h], i) => ({ c: [bc[0], bc[1] + h * rad, bc[2]] as V3, e1: H_E1, e2: H_E2, r: new Float32Array(BIN).fill(Math.max(0.0015, s * rad)), v: i / (prof.length - 1) }));
  const geo = loft(rings, 10, null);
  const chest = sk.byName.get('chest')!;
  const joints: number[] = [], weights: number[] = [];
  for (let i = 0; i < geo.positions.length / 3; i++) { joints.push(chest, 0, 0, 0); weights.push(1, 0, 0, 0); }
  return [collar, { piece: { id: 'bell', ...geo, joints, weights, style: S('#d9a92e', '#d9a92e', '#2b1d14', 4, 0) }, covers: () => false }];
}

/** Body triangle indices that stay visible under the outfit. */
export function visibleBodyIndices(body: Body, fits: Fit[]): Uint32Array {
  if (!fits.length) return body.indices;
  const vc = body.pos.length / 3;
  const covered = new Uint8Array(vc);
  for (let v = 0; v < vc; v++) for (const f of fits) if (f.covers(v)) { covered[v] = 1; break; }
  const out: number[] = [];
  for (let t = 0; t < body.indices.length; t += 3) {
    const a = body.indices[t], b = body.indices[t + 1], c = body.indices[t + 2];
    if (covered[a] && covered[b] && covered[c]) continue;
    out.push(a, b, c);
  }
  return new Uint32Array(out);
}
